import DeleteStaffMember from '@Application/Facilities/StaffMembers/StaffMembers/Commands/DeleteStaffMember';
import deleteStaffMember from '@Application/Facilities/StaffMembers/StaffMembers/deleteStaffMember';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import staffMemberToDeleteSignal, {
	setStaffMemberToDelete,
} from '@UI/Pages/Agencies/StaffMembers/Signals/staffMemberToDeleteSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog } from '@verclam/ui-kit';

/**
 * The delete confirmation, mounted unconditionally alongside the staff members list. Self-contained —
 * reads the list already fetched by `StaffMembersContent` through a plain `useSelector`, and the
 * pending-deletion signal, rather than through props.
 *
 * `linkedBeneficiaryCount` is served BY THE LIST already loaded (§5.1 of the plan): no extra request.
 * It comes from a `@Formula` with no tenant filter applied server-side, so it can exceed what the
 * caller can actually see — the second sentence exists precisely to say so, and is not decorative:
 * omitting it would understate the effect of the exact action the dialog warns about. Both sentences
 * disappear together when the count is `0`.
 */
const StaffMembersConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const staffMembers = useSelector((state) => state.staffMembersStore.staffMembers);
	const staffMemberToDelete = useSignalValue(staffMemberToDeleteSignal);

	const staffMemberBeingDeleted = React.useMemo(
		() => (staffMemberToDelete !== null ? (staffMembers?.findById(staffMemberToDelete) ?? null) : null),
		[staffMembers, staffMemberToDelete],
	);

	const handleDeleteDialogHide = React.useCallback(() => {
		setStaffMemberToDelete(null);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		if (organizationId === null || staffMemberToDelete === null) {
			return;
		}

		dispatch(deleteStaffMember(new DeleteStaffMember(organizationId, staffMemberToDelete))).catch(
			ignoreHandledError,
		);
		// Close immediately: `acceptCallback` never triggers `onHide`, and waiting for the
		// request to settle would leave the dialog open long enough for a second click.
		setStaffMemberToDelete(null);
	}, [organizationId, staffMemberToDelete, dispatch]);

	const linkedBeneficiaryCount = staffMemberBeingDeleted?.linkedBeneficiaryCount ?? 0;

	return (
		<ConfirmDeleteDialog
			visible={staffMemberToDelete !== null}
			onHide={handleDeleteDialogHide}
			header="Supprimer le membre du personnel"
			message={
				<React.Fragment>
					Supprimer définitivement <strong>{staffMemberBeingDeleted?.fullName ?? ''}</strong> ?{' '}
					{linkedBeneficiaryCount > 0 && (
						<React.Fragment>
							Il sera détaché de <strong>{linkedBeneficiaryCount}</strong> dossier(s), dont
							certains peuvent être hors de vos agences.
						</React.Fragment>
					)}
				</React.Fragment>
			}
			confirmText={staffMemberBeingDeleted?.fullName ?? ''}
			acceptCallback={handleDeleteAccept}
		/>
	);
};

export default StaffMembersConfirmationDialogs;
